import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft,
  X,
  StickyNote,
  Paperclip,
  Zap,
  SendHorizontal,
  User,
  Lock,
  Sparkles,
  Mic,
  XCircle,
  CheckCircle2,
  Plus,
  Loader2,
  MessageSquarePlus,
  Clock,
  FileText,
  UserPlus,
  Building2,
  Search,
  Check,
  Phone,
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import type { Conversation, Message } from '../../stores/whatsapp/chatStore';
import { useChatStore } from '../../stores/whatsapp/chatStore';
import { useMessages } from '../../hooks/whatsapp/useChat';
import { formatPhone, getConnectionLabel } from '../../services/whatsapp/waFormatters';
import Avatar from './WaAvatar';
import Badge from './WaBadge';
import MessageBubble from './MessageBubble';
import OwnershipBanner from './OwnershipBanner';
import TransferModal from './TransferModal';
import { useAuth } from '../../providers/auth';
import { useWaPermissions } from '../../hooks/whatsapp/useWaPermissions';
import { useQuickReplyStore, CATEGORY_LABELS } from '../../stores/whatsapp/quickReplyStore';
import { useWaAiSuggestions } from '../../hooks/whatsapp/useWaAiSuggestions';
import { uploadWhatsAppMedia, blobToFile } from '../../services/whatsapp/waUpload';
import { useKanbanStore } from '../../stores/whatsapp/kanbanStore';
import { useDeptVisibilityStore } from '../../stores/whatsapp/deptVisibilityStore';
import RatingRequestMessage from './RatingRequestMessage';
import SendTemplateModal from './SendTemplateModal';
import waApi from '../../services/whatsapp/waApi';
import { api } from '../../services/api';
import wsService from '../../services/whatsapp/websocket';

interface ChatWindowProps {
  conversationId: string;
  conversation: Conversation;
  onToggleClientPanel: () => void;
  onBack?: () => void;
}

function getDateLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

export default function ChatWindow({
  conversationId,
  conversation,
  onToggleClientPanel,
  onBack,
}: ChatWindowProps) {
  const { user } = useAuth();
  const permissions = useWaPermissions();
  const currentUserName = user?.nome || 'Usuario';
  const currentUserDept =
    (user as any)?.departamentos?.[0]?.nome || user?.departamento?.nome || null;
  const senderLabel = currentUserDept ? ` — ${currentUserDept}` : '';
  const { data: messages } = useMessages(conversationId);
  const addMessage = useChatStore(s => s.addMessage);
  const removeMessage = useChatStore(s => s.removeMessage);
  const updateConversation = useChatStore(s => s.updateConversation);

  // Kanban + IA
  const createKanbanFromConversation = useKanbanStore(s => s.createFromConversation);
  const completeKanbanTicket = useKanbanStore(s => s.completeTicket);
  const getKanbanByConversation = useKanbanStore(s => s.getByConversation);

  // Quick replies store
  const quickReplies = useQuickReplyStore(s => s.replies);
  const addQuickReply = useQuickReplyStore(s => s.addReply);
  const deleteQuickReply = useQuickReplyStore(s => s.deleteReply);
  // localMessages removido — tudo vai direto pro store via addMessage
  const [messageText, setMessageText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isAssuming, setIsAssuming] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Quick reply CRUD state
  const [newReplyText, setNewReplyText] = useState('');
  const [newReplyCategory, setNewReplyCategory] = useState('outro');
  const [showNewReplyForm, setShowNewReplyForm] = useState(false);

  // Comment on blocked input
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Backend saves assigned_agent_id as GUID, not numeric id
  const currentUserId = ((user as any)?.guid || user?.id)?.toString();
  const isCurrentUserOwner =
    !!conversation.assigned_agent_id &&
    (String(conversation.assigned_agent_id) === String(currentUserId) ||
      (!!conversation.assigned_agent_name &&
        conversation.assigned_agent_name.toLowerCase() === currentUserName.toLowerCase()));

  const [ownership, setOwnership] = useState({
    ownerName: conversation.assigned_agent_id
      ? conversation.assigned_agent_name || 'Atendente'
      : null,
    department: conversation.assigned_department || conversation.department || null,
    isOwner: isCurrentUserOwner,
    canTakeOver: !isCurrentUserOwner && !!conversation.assigned_agent_id,
    canTransfer: true,
    canRespond: isCurrentUserOwner,
  });

  // Finalizar chamado com análise da IA
  const [isClosing, setIsClosing] = useState(false);
  const [isClosed, setIsClosed] = useState(conversation.status === 'closed');
  const [conversationRating, setConversationRating] = useState<number | null>(
    conversation.rating ?? null,
  );

  // Contato não salvo: client_id igual ao telefone indica que não foi vinculado ao Indicadores
  const isContactUnsavedInitial =
    conversation.client_id === conversation.client_phone ||
    /^\d{10,}$/.test(conversation.client_id);
  const [contactSaved, setContactSaved] = useState(false);
  // Se o contato voltou a ser "não salvo" (ex.: foi excluído no admin e o
  // client_id da conversa voltou a ser o telefone), reabilita o banner —
  // sem isso a flag local de "salvo" ficava grudada e o CTA não reaparecia.
  useEffect(() => {
    if (isContactUnsavedInitial) {
      setContactSaved(false);
    }
  }, [isContactUnsavedInitial]);
  const isContactUnsaved = isContactUnsavedInitial && !contactSaved;
  const [showSaveContact, setShowSaveContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [saveContactDept, setSaveContactDept] = useState('suporte');
  const [saveContactEmpresaIds, setSaveContactEmpresaIds] = useState<number[]>([]);
  const [empresaOptions, setEmpresaOptions] = useState<{ id: number; nome: string }[]>([]);
  const [empresaSearch, setEmpresaSearch] = useState('');

  const DEPARTAMENTOS_SAVE = [
    { id: 'suporte', label: 'Suporte' },
    { id: 'fiscal', label: 'Fiscal' },
    { id: 'contabil', label: 'Contábil' },
    { id: 'pessoal', label: 'Depto. Pessoal' },
    { id: 'societario', label: 'Societário' },
    { id: 'financeiro', label: 'Financeiro' },
    { id: 'consultoria', label: 'Consultoria' },
  ];

  // Buscar empresas quando abre o popup de salvar contato
  useEffect(() => {
    if (!showSaveContact) return;
    api
      .get('/api/v1/empresas/', { params: { size: 50 } })
      .then(res => {
        const data = res.data?.data || res.data;
        const items = data?.items || (Array.isArray(data) ? data : []);
        setEmpresaOptions(
          items.map((e: any) => ({ id: e.id, nome: e.nome || e.razao_social || '' })),
        );
      })
      .catch(() => setEmpresaOptions([]));
  }, [showSaveContact]);

  const toggleSaveEmpresa = (id: number) => {
    setSaveContactEmpresaIds(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id],
    );
  };

  const handleSaveNewContact = async () => {
    if (saveContactEmpresaIds.length === 0) {
      toast.error('Selecione pelo menos uma empresa');
      return;
    }
    setSavingContact(true);
    try {
      const payload = {
        nome: conversation.client_name,
        telefone: conversation.client_phone,
        departamento: saveContactDept,
        tipo: 'contato',
        principal: false,
      };
      await Promise.all(
        saveContactEmpresaIds.map(eid => api.post(`/api/v1/empresas/${eid}/contatos/`, payload)),
      );
      // Revincular a conversa ao contato recém-criado no ContaChat: sem isso o
      // client_id da conversa continua sendo o telefone e o banner "contato não
      // salvo" reaparece após reload. O backend re-busca o contato no
      // Indicadores e persiste client_id/empresa/nome.
      try {
        await waApi.post(`/conversations/${conversationId}/relink-contact`);
      } catch (relinkErr) {
        // best-effort: contato já foi salvo; o webhook revincula na próxima msg
        toast.error(`Erro ao revincular conversa: ${relinkErr}`);
      }
      toast.success('Contato salvo com sucesso!');
      setShowSaveContact(false);
      setContactSaved(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar contato');
    } finally {
      setSavingContact(false);
    }
  };

  const effectiveStatus = isClosed ? 'closed' : conversation.status;

  // Situação do atendimento (issue #30): em vez do status cru "Aberta", exibe a
  // etapa real — encerrado; em atendimento (com atendente); ou aguardando na
  // fila (sem atendente). Usa assigned_agent_id (mesma chave do OwnershipBanner
  // do rodapé) para que os dois NUNCA divirjam — evita "Aberta" em cima e
  // "Em atendimento por X" embaixo.
  const attendanceState = (() => {
    if (effectiveStatus === 'closed') {
      return { label: 'Encerrado', variant: 'neutral' as const };
    }
    if (conversation.assigned_agent_id) {
      return { label: 'Em atendimento', variant: 'info' as const };
    }
    return { label: 'Aguardando na fila', variant: 'warning' as const };
  })();

  // Escuta avaliacao recebida via WebSocket
  useEffect(() => {
    const handleRatingReceived = (data: any) => {
      if (data?.conversation_id === conversationId && data?.rating) {
        setConversationRating(data.rating);
        toast.success(`Cliente avaliou: ${data.rating}/5`);
      }
    };
    const handleMessageDeleted = (data: any) => {
      if (data?.conversation_id === conversationId && data?.message_id) {
        removeMessage(conversationId, data.message_id);
      }
    };
    wsService.on('rating_received', handleRatingReceived);
    wsService.on('message_deleted', handleMessageDeleted);
    return () => {
      wsService.off('rating_received', handleRatingReceived);
      wsService.off('message_deleted', handleMessageDeleted);
    };
  }, [conversationId, removeMessage]);

  // Subscribe to conversation for real-time updates (retry if WS not connected yet)
  useEffect(() => {
    wsService.subscribe(conversationId);
    // Re-subscribe when WS reconnects (handles race condition on first load)
    const handleReconnect = () => wsService.subscribe(conversationId);
    wsService.onOpen(handleReconnect);
    return () => {
      wsService.offOpen(handleReconnect);
    };
  }, [conversationId]);

  const handleReopenAndAssume = async () => {
    if (isAssuming) return;
    setIsAssuming(true);
    try {
      await waApi.post(`/conversations/${conversationId}/assume`);

      // Conversation reopened with history preserved
      setIsClosed(false);
      updateConversation(conversationId, {
        assigned_agent_id: currentUserId,
        assigned_agent_name: currentUserName,
        assigned_department: currentUserDept || undefined,
        status: 'open',
      });
      setOwnership({
        ownerName: currentUserName,
        department: currentUserDept,
        isOwner: true,
        canTakeOver: false,
        canTransfer: true,
        canRespond: true,
      });
      toast.success('Chamado reaberto!');
    } catch {
      toast.error('Erro ao assumir conversa. Tente novamente.');
    } finally {
      setIsAssuming(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [messageText]);

  // Reset state when conversation changes
  useEffect(() => {
    const isOwner =
      !!conversation.assigned_agent_id &&
      (String(conversation.assigned_agent_id) === String(currentUserId) ||
        (!!conversation.assigned_agent_name &&
          conversation.assigned_agent_name.toLowerCase() === currentUserName.toLowerCase()));
    setShowQuickReplies(false);
    setShowAiSuggestions(false);
    setIsClosed(conversation.status === 'closed');
    setOwnership({
      ownerName: conversation.assigned_agent_id
        ? conversation.assigned_agent_name || 'Atendente'
        : null,
      department: conversation.assigned_department || conversation.department || null,
      isOwner,
      canTakeOver: !isOwner && !!conversation.assigned_agent_id,
      canTransfer: true,
      canRespond: isOwner,
    });
  }, [
    conversationId,
    conversation.status,
    conversation.assigned_agent_id,
    conversation.assigned_agent_name,
    conversation.assigned_department,
    conversation.department,
    currentUserId,
    currentUserName,
  ]);

  // Sugestoes de IA via endpoint real
  const {
    suggestions: aiSuggestions,
    isLoading: aiLoading,
    fetchSuggestions,
  } = useWaAiSuggestions(messages, conversation.subject);

  const handleSend = async () => {
    if (!messageText.trim()) return;

    const content = messageText.trim();
    setMessageText('');
    setIsInternalNote(false);
    setShowQuickReplies(false);
    setShowAiSuggestions(false);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Prefixar nome do agente para o cliente saber quem enviou (apenas msgs normais)
    const finalContent = isInternalNote
      ? content
      : `*${currentUserName}${senderLabel}:*\n${content}`;

    // Enviar para o backend para persistir no MongoDB
    try {
      const res = await waApi.post('/messages', {
        conversation_id: conversationId,
        content: finalContent,
        message_type: 'text',
        is_internal_note: isInternalNote,
      });
      const saved = res.data;
      const persistedMsg: Message = {
        // eslint-disable-next-line no-underscore-dangle
        id: saved._id || saved.id || `local-${Date.now()}`,
        conversation_id: conversationId,
        sender_type: 'agent',
        sender_id: currentUserId || 'current-user',
        content: finalContent,
        message_type: 'text',
        status: saved.status || 'sent',
        is_internal_note: isInternalNote,
        created_at: saved.created_at || new Date().toISOString(),
      };
      addMessage(conversationId, persistedMsg);
    } catch {
      // Fallback: adiciona localmente se API falhar
      const localMsg: Message = {
        id: `local-${Date.now()}`,
        conversation_id: conversationId,
        sender_type: 'agent',
        sender_id: currentUserId || 'current-user',
        content,
        message_type: 'text',
        status: 'sent',
        is_internal_note: isInternalNote,
        created_at: new Date().toISOString(),
      };
      addMessage(conversationId, localMsg);
    }

    if (isInternalNote) {
      toast.success('Nota interna adicionada');
    }
  };

  const handleQuickReply = (text: string) => {
    setMessageText(text);
    setShowQuickReplies(false);
    setShowAiSuggestions(false);
    textareaRef.current?.focus();
  };

  // Gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Meta API aceita ogg/opus; webm como fallback
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')
        ? 'audio/ogg; codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = e => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        const ext = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunks, { type: mimeType });
        const filename = `audio_${Date.now()}.${ext}`;
        const file = blobToFile(blob, filename);

        const s3Result = await uploadWhatsAppMedia(file, conversationId);
        stream.getTracks().forEach(t => t.stop());

        if (!s3Result?.url) {
          toast.error('Erro no upload do audio');
          return;
        }

        try {
          const res = await waApi.post('/messages', {
            conversation_id: conversationId,
            content: '',
            message_type: 'audio',
            is_internal_note: false,
            media_url: s3Result.url,
            media_mime_type: mimeType,
          });
          const saved = res.data;
          addMessage(conversationId, {
            // eslint-disable-next-line no-underscore-dangle
            id: saved._id || saved.id || `audio-${Date.now()}`,
            conversation_id: conversationId,
            sender_type: 'agent',
            sender_id: currentUserId || 'current-user',
            content: '',
            message_type: 'audio',
            status: saved.status || 'sent',
            is_internal_note: false,
            created_at: saved.created_at || new Date().toISOString(),
            media_url: s3Result.url,
            media_filename: s3Result.filename || filename,
            media_mime_type: mimeType,
          });
          toast.success('Áudio enviado');
        } catch {
          toast.error('Erro ao enviar áudio');
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error('Não foi possível acessar o microfone');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
    if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    setRecordingTime(0);
  };

  // Anexar arquivo — upload S3 + persistir no backend
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImg = file.type.startsWith('image/');
    const isAud = file.type.startsWith('audio/');
    let msgType: string;
    if (isImg) {
      msgType = 'image';
    } else if (isAud) {
      msgType = 'audio';
    } else {
      msgType = 'document';
    }

    // Upload S3
    const s3Result = await uploadWhatsAppMedia(file, conversationId);
    if (!s3Result?.url) {
      toast.error('Erro no upload do arquivo');
      e.target.value = '';
      return;
    }

    try {
      const res = await waApi.post('/messages', {
        conversation_id: conversationId,
        content: `*${currentUserName}${senderLabel}:*\n${isImg ? '' : file.name}`.trim(),
        message_type: msgType,
        is_internal_note: false,
        media_url: s3Result.url,
        media_mime_type: file.type,
      });
      const saved = res.data;
      const mediaContent = `*${currentUserName}${senderLabel}:*\n${isImg ? '' : file.name}`.trim();
      addMessage(conversationId, {
        // eslint-disable-next-line no-underscore-dangle
        id: saved._id || saved.id || `file-${Date.now()}`,
        conversation_id: conversationId,
        sender_type: 'agent',
        sender_id: currentUserId || 'current-user',
        content: mediaContent,
        message_type: msgType as any,
        status: saved.status || 'sent',
        is_internal_note: false,
        created_at: saved.created_at || new Date().toISOString(),
        media_url: s3Result.url,
        media_filename: s3Result.filename || file.name,
        media_mime_type: file.type,
        media_size: file.size,
      });
      toast.success(`${file.name} enviado`);
    } catch {
      toast.error('Erro ao enviar arquivo');
    }
    e.target.value = '';
  };

  const handleCloseTicket = async () => {
    if (isClosing || isClosed) return;
    setIsClosing(true);

    try {
      // Fecha no backend — ele cuida de tudo (protocolo, msgs, IA, WhatsApp).
      // A decisão de pedir avaliação agora é do backend, que consulta o campo
      // avaliacao_desativada do cadastro do contato (issue #42). Não há mais
      // controle por conversa aqui — enviamos skip_rating=false e o ContaChat
      // sobrescreve conforme o cadastro.
      const closeRes = await waApi.post(`/conversations/${conversationId}/close`, {
        skip_rating: false,
      });

      const protocol = closeRes.data?.ticket_protocol || '';
      if (protocol) setTicketProtocol(protocol);

      // Move card do Kanban para "done"
      const kanbanTicket = getKanbanByConversation(conversationId);
      if (kanbanTicket) {
        completeKanbanTicket(kanbanTicket.id, '');
      }

      setIsClosed(true);
      updateConversation(conversationId, { status: 'closed' });
      toast.success(`Chamado finalizado — Protocolo: ${protocol}`);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Erro ao finalizar chamado');
    } finally {
      setIsClosing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const generateProtocol = () => {
    const now = new Date();
    const y = now.getFullYear();
    const ts = Date.now().toString(36).toUpperCase().slice(-5);
    return `ATD-${y}-${ts}`;
  };

  const [ticketProtocol, setTicketProtocol] = useState(
    conversation.ticket_protocol || conversation.ticket_number || '',
  );

  const addClientVisibleMessage = (text: string) => {
    const msg: Message = {
      id: `bot-${Date.now()}`,
      conversation_id: conversationId,
      sender_type: 'bot',
      content: text,
      message_type: 'text',
      status: 'sent',
      is_internal_note: false,
      created_at: new Date().toISOString(),
    };
    addMessage(conversationId, msg);
  };

  const handleAssume = async () => {
    if (isAssuming) return;
    setIsAssuming(true);
    try {
      const protocol = ticketProtocol || generateProtocol();

      // Tenta persistir no backend
      try {
        await waApi.post(`/conversations/${conversationId}/assume`);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403 || status === 409) {
          const detail = err?.response?.data?.detail || 'Sem permissão para assumir';
          toast.error(detail);
          return;
        }
        // Fallback: try assign endpoint for older backends
        try {
          await waApi.post(`/conversations/${conversationId}/assign`, {
            agent_id: currentUserId,
            agent_name: currentUserName,
          });
        } catch {
          toast.error('Erro ao assumir conversa. Tente novamente.');
          return;
        }
      }

      // O histórico interno (mensagem cinza) agora é gerado pelo backend e chega
      // por WebSocket (new_message) com depto/conexão/status — issue #39. Não
      // criamos mais a system message localmente para não duplicar.
      addClientVisibleMessage(
        `Ola! Seu atendimento foi iniciado.\n\nProtocolo: *${protocol}*\nAtendente: ${currentUserName}\n\nComo posso ajuda-lo(a)?`,
      );

      // Cria card automaticamente no Kanban WhatsApp
      createKanbanFromConversation(conversation, currentUserName);

      // Atualiza a conversa no store
      updateConversation(conversationId, {
        assigned_agent_id: currentUserId,
        assigned_agent_name: currentUserName,
        assigned_department: currentUserDept || undefined,
        status: 'open',
      });

      setOwnership({
        ownerName: currentUserName,
        department: currentUserDept,
        isOwner: true,
        canTakeOver: false,
        canTransfer: true,
        canRespond: true,
      });
      toast.success('Atendimento iniciado! Card criado no Kanban.');
    } finally {
      setIsAssuming(false);
    }
  };

  const handleTakeOver = async () => {
    // Se o backend rejeitar (ex.: 403/404 — sem permissão para o departamento
    // da conversa), aborta sem atualizar o estado local, para a UI nao mostrar
    // como assumido algo que o backend rejeitou.
    try {
      await waApi.post(`/conversations/${conversationId}/assign`, {
        agent_id: currentUserId,
        agent_name: currentUserName,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Nao foi possivel assumir o atendimento.');
      return;
    }

    const protocol = ticketProtocol || generateProtocol();

    // Histórico interno (cinza) vem do backend via WebSocket — issue #39.
    addClientVisibleMessage(
      `Seu atendimento foi transferido.\n\nProtocolo: *${protocol}*\nNovo atendente: ${currentUserName}\n\nComo posso ajuda-lo(a)?`,
    );

    // Atualiza a conversa no store
    updateConversation(conversationId, {
      assigned_agent_id: currentUserId,
      assigned_agent_name: currentUserName,
      assigned_department: currentUserDept || undefined,
      status: 'open',
    });

    setOwnership({
      ownerName: currentUserName,
      department: currentUserDept,
      isOwner: true,
      canTakeOver: false,
      canTransfer: true,
      canRespond: true,
    });
  };

  const handleTransfer = async (
    agentId: string | null,
    agentName: string | null,
    department: string,
    reason: string,
  ) => {
    const destLabel = agentName ? `${agentName} (${department})` : `departamento ${department}`;

    // Persiste no backend. Se falhar (ex.: 403 — sem permissão para o
    // departamento de destino), aborta sem atualizar o estado local, para a UI
    // nao mostrar como transferido algo que o backend rejeitou.
    try {
      await waApi.post(`/conversations/${conversationId}/transfer`, {
        agent_id: agentId,
        agent_name: agentName,
        department,
        reason,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Nao foi possivel transferir o atendimento.');
      return;
    }

    // Histórico interno (cinza) vem do backend via WebSocket — issue #39.

    // Atualiza a conversa no store para refletir na lista imediatamente
    updateConversation(conversationId, {
      assigned_agent_id: agentId || undefined,
      assigned_agent_name: agentName || undefined,
      department,
      assigned_department: department,
    });

    setOwnership({
      ownerName: agentName,
      department,
      isOwner: false,
      canTakeOver: true,
      canTransfer: true,
      canRespond: false,
    });
    toast.success(`Transferido para ${destLabel}`);
  };

  // Enviar comentario interno (disponivel mesmo com input bloqueado)
  const handleSendComment = () => {
    if (!commentText.trim()) return;
    const msg: Message = {
      id: `note-${Date.now()}`,
      conversation_id: conversationId,
      sender_type: 'agent',
      sender_id: 'current-user',
      content: commentText.trim(),
      message_type: 'text',
      status: 'sent',
      is_internal_note: true,
      created_at: new Date().toISOString(),
    };
    addMessage(conversationId, msg);
    setCommentText('');
    setShowCommentInput(false);
    toast.success('Comentario interno adicionado');
  };

  // Avaliacao chega via WebSocket (rating_received) — apenas exibe readonly
  // Nao ha mais interacao manual do agente

  // Visibilidade por departamento
  const canSeeDept = useDeptVisibilityStore(s => s.canSeeDept);
  const deptRestricted = useDeptVisibilityStore(s => s.restrictByDept);
  const blurMessages = useDeptVisibilityStore(s => s.blurMessages);
  // Verifica departamento usando logica local (fallback robusto)
  // para nao depender apenas do store que pode ter timing issues
  const isVisibleDept = (dept: string): boolean => {
    if (!dept) return true;
    // Primeiro tenta o store
    if (canSeeDept(dept)) return true;
    // Fallback: compara diretamente com o grupo do usuario
    if (!currentUserDept) return true;
    const normalize = (s: string) => s.toLowerCase().trim();
    const u = normalize(currentUserDept);
    const d = normalize(dept);
    if (u === d) return true;
    if (u.includes(d) || d.includes(u)) return true;
    const uWords = u.split(/[\s.,-]+/).filter(Boolean);
    const dWords = d.split(/[\s.,-]+/).filter(Boolean);
    if (uWords[0] === dWords[0]) return true;
    return false;
  };

  // Detecta sessoes de departamento para redacao.
  const buildRedactedMap = (): Set<string> => {
    // Sem restricao: mostra tudo
    if (!deptRestricted) return new Set();
    const redacted = new Set<string>();

    // Verifica se existem marcadores de sessao na conversa
    const hasSessionMarkers = messages.some(
      m =>
        m.sender_type === 'system' &&
        (m.content.includes('Atendimento iniciado') || m.content.includes('assumiu o atendimento')),
    );

    if (hasSessionMarkers) {
      const convFallbackDept = conversation.assigned_department || conversation.department || null;
      const userNameLower = currentUserName.toLowerCase();
      let isRedactedSession = false;

      messages.forEach(msg => {
        if (msg.sender_type === 'system') {
          if (
            msg.content.includes('Atendimento iniciado') ||
            msg.content.includes('assumiu o atendimento')
          ) {
            const isOwnSession = msg.content.toLowerCase().includes(userNameLower);
            if (isOwnSession) {
              isRedactedSession = false;
            } else {
              const deptMatch = msg.content.match(/—\s*(.+?)(?:\s*$|\s*—)/);
              const sessionDept = deptMatch ? deptMatch[1].trim() : convFallbackDept;
              isRedactedSession = sessionDept ? !isVisibleDept(sessionDept) : false;
            }
          }
          if (msg.content.includes('Chamado encerrado') || msg.content.includes('Transferido')) {
            isRedactedSession = false;
          }
        } else if (isRedactedSession && blurMessages) {
          redacted.add(msg.id);
        }
      });
    } else {
      // Modo sem marcadores de sessao
      if (ownership.isOwner) return new Set();

      const convDept =
        conversation.assigned_department ||
        conversation.department ||
        (() => {
          const text =
            `${conversation.subject || ''} ${conversation.last_message || ''}`.toLowerCase();
          if (
            text.includes('irpf') ||
            text.includes('fiscal') ||
            text.includes('cnd') ||
            text.includes('nota')
          )
            return 'Fiscal';
          if (text.includes('folha') || text.includes('pessoal') || text.includes('ferias'))
            return 'Depto. Pessoal';
          if (text.includes('contabil') || text.includes('balancete')) return 'Contabil';
          if (text.includes('societario') || text.includes('abertura')) return 'Societario';
          if (text.includes('comercial') || text.includes('plano') || text.includes('proposta'))
            return 'Comercial';
          return null;
        })();

      if (convDept && !isVisibleDept(convDept) && blurMessages) {
        messages.forEach(msg => {
          if (msg.sender_type !== 'system') {
            redacted.add(msg.id);
          }
        });
      }
    }

    return redacted;
  };
  const redactedMsgIds = buildRedactedMap();

  // Agrupar mensagens por data
  const messagesWithDates: { type: 'date' | 'message'; content: string; message?: Message }[] = [];
  let lastDate = '';
  messages.forEach(msg => {
    const dateKey = msg.created_at.slice(0, 10);
    if (dateKey !== lastDate) {
      lastDate = dateKey;
      messagesWithDates.push({ type: 'date', content: getDateLabel(msg.created_at) });
    }
    messagesWithDates.push({ type: 'message', content: '', message: msg });
  });

  const isOtherAgent = !ownership.isOwner && ownership.ownerName !== null;
  const isUnassigned = !ownership.isOwner && !ownership.ownerName;
  const inputBlocked = isClosed || isOtherAgent || isUnassigned || !permissions.canResponder;

  // Detectar janela de 24h da Meta
  const isOutside24hWindow = (() => {
    const lastClientMsg = [...messages].reverse().find(m => m.sender_type === 'client');
    if (!lastClientMsg) return true;
    const lastClientTime = new Date(lastClientMsg.created_at).getTime();
    const now = Date.now();
    return now - lastClientTime > 24 * 60 * 60 * 1000;
  })();

  // Permissoes granulares para assumir/transferir
  const isSameDept = (() => {
    if (!currentUserDept || !ownership.department) return false;
    const u = currentUserDept.toLowerCase().trim();
    const d = ownership.department.toLowerCase().trim();
    return u === d || u.includes(d) || d.includes(u);
  })();

  // Pode assumir: depende do estado da conversa
  const canAssumeThis = (() => {
    if (ownership.isOwner) return false; // ja e meu
    if (!ownership.ownerName) {
      // Fila — sem dono
      return permissions.canAssumirFila || permissions.canAssumirTodos;
    }
    // Tem dono
    if (isSameDept) return permissions.canAssumirDepartamento || permissions.canAssumirTodos;
    return permissions.canAssumirTodos;
  })();

  // Pode transferir: depende de quem e o dono
  const canTransferThis = (() => {
    if (ownership.isOwner)
      return permissions.canTransferirProprio || permissions.canTransferirTodos;
    if (isSameDept) return permissions.canTransferirDepartamento || permissions.canTransferirTodos;
    return permissions.canTransferirTodos;
  })();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="z-10 flex items-center gap-2 border-b border-gray-200 bg-white px-3 py-2.5 md:px-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 rounded-full p-2 text-green-600 transition-colors hover:bg-green-50 md:hidden"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}

        {/* Esquerda: avatar + nome + telefone (issue #30) */}
        <button
          type="button"
          onClick={onToggleClientPanel}
          className="flex min-w-0 items-center gap-3 overflow-hidden text-left"
        >
          <div className="flex-shrink-0">
            <Avatar name={conversation.client_name} size="md" online />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-800">
              {conversation.client_name}
            </h3>
            <p className="truncate text-xs text-gray-400">
              {formatPhone(conversation.client_phone)}
            </p>
          </div>
        </button>

        {/* Centro: situação do atendimento + conexão, empilhadas e centralizadas
            (issue #30/#40). O "via {nome}" só aparece quando há conexão real. */}
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-2 text-center">
          <Badge text={attendanceState.label} variant={attendanceState.variant} size="sm" />
          {getConnectionLabel(conversation) && (
            <span className="mt-0.5 max-w-full truncate text-xs text-green-500">
              via {getConnectionLabel(conversation)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onToggleClientPanel}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600"
            title="Info do cliente"
          >
            <User className="h-4 w-4" />
          </button>
          {/*
            Avaliação ao finalizar (issue #42): o controle deixou de ser por
            conversa (estrela aqui) e passou a ser um campo do cadastro do
            contato (avaliacao_desativada). O ContaChat consulta esse campo ao
            encerrar. O indicador do estado fica no painel Info do Cliente.
          */}
          {!isClosed && permissions.canFinalizar && ownership.isOwner && (
            <button
              type="button"
              onClick={handleCloseTicket}
              disabled={isClosing}
              className={`rounded-full p-2 transition-colors ${isClosing ? 'animate-spin text-orange-400' : 'text-gray-400 hover:bg-red-50 hover:text-red-500'}`}
              title="Finalizar chamado"
            >
              {isClosing ? (
                <CheckCircle2 className="h-4 w-4 animate-pulse" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Ownership Banner */}
      <OwnershipBanner
        ownerName={ownership.ownerName}
        department={
          ownership.department ||
          conversation.assigned_department ||
          conversation.department ||
          null
        }
        isOwner={ownership.isOwner}
        canTakeOver={isOtherAgent && canAssumeThis}
        canTransfer={canTransferThis}
        canAssume={!ownership.ownerName && canAssumeThis}
        isClosed={isClosed}
        isReopening={isAssuming}
        onAssume={handleAssume}
        onTransfer={() => setShowTransfer(true)}
        onTakeOver={handleTakeOver}
        onReopen={handleReopenAndAssume}
      />

      {/* Banner: contato não salvo */}
      {isContactUnsaved && !isClosed && (
        <div className="flex items-center justify-between border-b border-purple-100 bg-purple-50 px-4 py-2">
          <div className="flex items-center gap-2">
            <UserPlus size={14} className="text-purple-500" />
            <span className="text-xs font-medium text-purple-700">
              Contato ainda não salvo no sistema.
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowSaveContact(true)}
            className="rounded-lg bg-purple-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-purple-700"
          >
            Salvar Contato
          </button>
        </div>
      )}

      {/* Popup salvar contato */}
      {showSaveContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay como botao (fecha ao clicar/Enter) — a11y sem handler em div */}
          <button
            type="button"
            aria-label="Fechar"
            disabled={savingContact}
            onClick={() => setShowSaveContact(false)}
            className="absolute inset-0 cursor-default bg-gray-900/50 backdrop-blur-sm"
          />
          <div
            className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
            role="dialog"
            aria-modal="true"
          >
            {/* Header com gradiente */}
            <div className="relative bg-gradient-to-br from-purple-600 to-purple-500 px-6 pb-5 pt-6 text-white">
              <button
                type="button"
                onClick={() => setShowSaveContact(false)}
                disabled={savingContact}
                className="absolute right-4 top-4 rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20">
                  <UserPlus size={22} />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold leading-tight">
                    {conversation.client_name}
                  </h3>
                  <p className="flex items-center gap-1 text-xs text-white/80">
                    <Phone size={11} />
                    {formatPhone(conversation.client_phone)}
                  </p>
                </div>
              </div>
            </div>

            {/* Corpo */}
            <div className="space-y-4 px-6 py-5">
              {/* Empresas (multi-select com busca) */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Building2 size={13} /> Empresas
                  </span>
                  {saveContactEmpresaIds.length > 0 && (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
                      {saveContactEmpresaIds.length} selecionada
                      {saveContactEmpresaIds.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={empresaSearch}
                    onChange={e => setEmpresaSearch(e.target.value)}
                    placeholder="Buscar empresa..."
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-purple-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
                <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5">
                  {empresaOptions.length === 0 && (
                    <p className="flex items-center justify-center gap-2 px-2 py-6 text-xs text-gray-400">
                      <Loader2 size={14} className="animate-spin" /> Carregando empresas...
                    </p>
                  )}
                  {empresaOptions
                    .filter(emp => emp.nome.toLowerCase().includes(empresaSearch.toLowerCase()))
                    .map(emp => {
                      const selected = saveContactEmpresaIds.includes(emp.id);
                      return (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => toggleSaveEmpresa(emp.id)}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                            selected
                              ? 'bg-purple-50 text-purple-800'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span
                            className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                              selected
                                ? 'border-purple-600 bg-purple-600 text-white'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {selected && <Check size={11} strokeWidth={3} />}
                          </span>
                          <span className="truncate">{emp.nome}</span>
                        </button>
                      );
                    })}
                  {empresaOptions.length > 0 &&
                    empresaOptions.filter(emp =>
                      emp.nome.toLowerCase().includes(empresaSearch.toLowerCase()),
                    ).length === 0 && (
                      <p className="px-2 py-6 text-center text-xs text-gray-400">
                        Nenhuma empresa encontrada
                      </p>
                    )}
                </div>
              </div>

              {/* Departamento */}
              <div>
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Departamento
                </span>
                <select
                  value={saveContactDept}
                  onChange={e => setSaveContactDept(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  {DEPARTAMENTOS_SAVE.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowSaveContact(false)}
                disabled={savingContact}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveNewContact}
                disabled={savingContact || saveContactEmpresaIds.length === 0}
                className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingContact ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Salvando...
                  </>
                ) : (
                  <>
                    <Check size={15} /> Salvar contato
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-3 py-4 md:px-4">
        <div className="mx-auto max-w-3xl space-y-1">
          {messagesWithDates.map((item, i) => {
            if (item.type === 'date') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`date-${i}`} className="flex justify-center py-3">
                  <span className="rounded-full bg-white px-4 py-1.5 text-xs font-medium text-gray-500 shadow-sm">
                    {item.content}
                  </span>
                </div>
              );
            }
            return (
              <MessageBubble
                key={item.message!.id}
                message={item.message!}
                isOwn={item.message!.sender_type === 'agent'}
                redacted={redactedMsgIds.has(item.message!.id)}
              />
            );
          })}

          {/* Rating widget — readonly, exibe nota recebida via WhatsApp */}
          {isClosed && conversationRating && (
            <RatingRequestMessage currentRating={conversationRating} readonly />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 24h window warning */}
      {isOutside24hWindow && !inputBlocked && (
        <div className="flex items-center gap-2 border-t border-orange-200 bg-orange-50 px-4 py-2">
          <Clock size={14} className="flex-shrink-0 text-orange-500" />
          <span className="text-xs text-orange-600">
            Cliente inativo há mais de 24h — mensagens de texto não serão entregues.
          </span>
          <button
            type="button"
            onClick={() => setShowTemplateModal(true)}
            className="ml-auto flex-shrink-0 rounded-lg bg-orange-500 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-orange-600"
          >
            Enviar Template
          </button>
        </div>
      )}

      {/* Internal note indicator */}
      {isInternalNote && (
        <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-4 py-1.5">
          <StickyNote className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-medium text-amber-600">
            Nota interna (não será enviada ao cliente)
          </span>
          <button
            type="button"
            onClick={() => setIsInternalNote(false)}
            className="ml-auto text-amber-400 hover:text-amber-600"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Quick replies / AI suggestions panel */}
      {(showQuickReplies || showAiSuggestions) && (
        <div className="max-h-56 overflow-y-auto border-t border-gray-200 bg-white px-3 py-3 md:px-4">
          <div className="mx-auto max-w-3xl">
            {showAiSuggestions && (
              <div className="mb-3">
                <div className="mb-2 flex items-center gap-1.5">
                  <Sparkles size={13} className="text-purple-500" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-purple-600">
                    Sugestoes da IA
                  </span>
                  {aiLoading && <Loader2 size={12} className="animate-spin text-purple-400" />}
                </div>
                {aiSuggestions.length > 0 ? (
                  <div className="flex flex-col gap-1.5">
                    {aiSuggestions.map((suggestion, i) => (
                      <button
                        type="button"
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        onClick={() => handleQuickReply(suggestion)}
                        className="rounded-xl border border-purple-100 bg-purple-50 px-3 py-2 text-left text-sm text-purple-800 transition-colors hover:bg-purple-100"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : (
                  !aiLoading && (
                    <p className="text-xs text-gray-400">
                      Clique no botao para gerar sugestoes baseadas na conversa.
                    </p>
                  )
                )}
              </div>
            )}

            {showQuickReplies && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Zap size={13} className="text-green-500" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-green-600">
                      Respostas rapidas
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewReplyForm(!showNewReplyForm);
                      setNewReplyText('');
                      setNewReplyCategory('outro');
                    }}
                    className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-green-600 transition-colors hover:bg-green-50"
                  >
                    <Plus size={12} />
                    Nova
                  </button>
                </div>

                {/* Form nova resposta rapida */}
                {showNewReplyForm && (
                  <div className="mb-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={newReplyText}
                      onChange={e => setNewReplyText(e.target.value)}
                      placeholder="Texto da resposta..."
                      className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-green-300"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newReplyText.trim()) {
                          addQuickReply(newReplyText.trim(), newReplyCategory, currentUserName);
                          setNewReplyText('');
                          setShowNewReplyForm(false);
                        }
                      }}
                    />
                    <select
                      value={newReplyCategory}
                      onChange={e => setNewReplyCategory(e.target.value)}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs outline-none"
                    >
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v as string}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        if (newReplyText.trim()) {
                          addQuickReply(newReplyText.trim(), newReplyCategory, currentUserName);
                          setNewReplyText('');
                          setShowNewReplyForm(false);
                        }
                      }}
                      className="rounded-lg p-1.5 text-green-600 hover:bg-green-50"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5">
                  {quickReplies.map(qr => (
                    <div key={qr.id} className="group relative">
                      <button
                        type="button"
                        onClick={() => handleQuickReply(qr.text)}
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 pr-8 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                      >
                        {qr.text.length > 50 ? `${qr.text.slice(0, 50)}...` : qr.text}
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          deleteQuickReply(qr.id);
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                        title="Excluir"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input area */}
      {inputBlocked ? (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
          {showCommentInput ? (
            /* Form de comentario interno */
            <div className="mx-auto max-w-3xl">
              <div className="mb-2 flex items-center gap-1.5">
                <StickyNote size={13} className="text-amber-500" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-600">
                  Comentario interno
                </span>
                <button
                  type="button"
                  onClick={() => setShowCommentInput(false)}
                  className="ml-auto text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  ref={commentRef}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendComment();
                    }
                  }}
                  placeholder="Escreva um comentario interno..."
                  rows={1}
                  className="max-h-[80px] flex-1 resize-none rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-300"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSendComment}
                  disabled={!commentText.trim()}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm transition-all hover:bg-amber-600 disabled:opacity-50"
                >
                  <SendHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            /* Mensagem de bloqueio + botao comentario */
            <div className="mx-auto flex max-w-3xl items-center justify-center gap-3">
              <div className="flex items-center gap-2 text-gray-400">
                <Lock size={16} />
                {(() => {
                  if (isClosed) return <span className="text-sm">Chamado encerrado.</span>;
                  if (isUnassigned)
                    return <span className="text-sm">Assuma o chamado para responder</span>;
                  if (isOtherAgent) {
                    return (
                      <span className="text-sm">
                        Em atendimento por{' '}
                        <strong className="text-gray-500">{ownership.ownerName}</strong>
                      </span>
                    );
                  }
                  return <span className="text-sm">Sem permissão para enviar mensagens</span>;
                })()}
              </div>
              {permissions.canNotaInterna && !isClosed && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCommentInput(true);
                    setTimeout(() => commentRef.current?.focus(), 100);
                  }}
                  className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-600 transition-colors hover:bg-amber-100"
                >
                  <MessageSquarePlus size={14} />
                  Comentar
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="border-t border-gray-200 bg-white px-3 py-2.5 md:px-4">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            className="hidden"
            onChange={handleFileSelect}
          />

          {isRecording ? (
            /* Gravando áudio */
            <div className="mx-auto flex max-w-3xl items-center gap-3">
              <button
                type="button"
                onClick={cancelRecording}
                className="rounded-full p-2 text-red-500 hover:bg-red-50"
                title="Cancelar"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex flex-1 items-center gap-3">
                <div className="h-3 w-3 animate-pulse rounded-full bg-red-500" />
                <span className="text-sm font-medium text-red-600">
                  {Math.floor(recordingTime / 60)}:
                  {(recordingTime % 60).toString().padStart(2, '0')}
                </span>
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-red-100">
                  <div
                    className="h-full animate-pulse rounded-full bg-red-400"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={stopRecording}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-green-600 text-white shadow-sm hover:bg-green-700"
                title="Enviar áudio"
              >
                <SendHorizontal className="h-5 w-5" />
              </button>
            </div>
          ) : (
            /* Input normal */
            <div className="mx-auto flex max-w-3xl items-end gap-1.5">
              {/* Botoes esquerda: mic, nota, anexo, rapidas, IA */}
              <div className="flex flex-shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={startRecording}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600"
                  title="Gravar audio"
                >
                  <Mic className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsInternalNote(!isInternalNote)}
                  className={`rounded-full p-2 transition-all ${isInternalNote ? 'bg-amber-100 text-amber-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                  title="Nota interna"
                >
                  <StickyNote className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  title="Anexar arquivo"
                >
                  <Paperclip className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickReplies(!showQuickReplies);
                    setShowAiSuggestions(false);
                  }}
                  className={`rounded-full p-2 transition-all ${showQuickReplies ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                  title="Respostas rapidas"
                >
                  <Zap className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(true)}
                  className={`rounded-full p-2 transition-all ${isOutside24hWindow ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                  title={
                    isOutside24hWindow ? 'Fora da janela 24h — use template' : 'Enviar template'
                  }
                >
                  <FileText className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = !showAiSuggestions;
                    setShowAiSuggestions(next);
                    setShowQuickReplies(false);
                    if (next) fetchSuggestions();
                  }}
                  className={`rounded-full p-2 transition-all ${showAiSuggestions ? 'bg-purple-100 text-purple-600' : 'text-purple-400 hover:bg-purple-50 hover:text-purple-600'}`}
                  title="Sugestoes da IA"
                >
                  {aiLoading ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  ) : (
                    <Sparkles className="h-[18px] w-[18px]" />
                  )}
                </button>
              </div>

              <textarea
                ref={textareaRef}
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isInternalNote ? 'Escreva uma nota interna...' : 'Digite uma mensagem...'
                }
                rows={1}
                className={`max-h-[120px] flex-1 resize-none rounded-2xl border px-4 py-2.5 text-sm outline-none transition-all placeholder:text-gray-400 focus:ring-2 ${
                  isInternalNote
                    ? 'border-amber-200 bg-amber-50 focus:ring-amber-300'
                    : 'border-transparent bg-gray-100 focus:bg-white focus:ring-green-300'
                }`}
              />

              {/* Enviar */}
              <button
                type="button"
                onClick={handleSend}
                disabled={!messageText.trim()}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-600 text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-40 disabled:hover:bg-green-600"
                title="Enviar"
              >
                <SendHorizontal className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Send Template Modal */}
      <SendTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        conversationId={conversationId}
        clientName={conversation.client_name}
        onSent={() => {
          // Recarrega mensagens após envio de template
          setShowTemplateModal(false);
        }}
      />

      {/* Transfer Modal */}
      <TransferModal
        isOpen={showTransfer}
        onClose={() => setShowTransfer(false)}
        onTransfer={handleTransfer}
      />
    </div>
  );
}
